import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setHeaderDetails } from "store/actions/header";
import { uploadFile } from "store/actions/program";
import { getProfile, updateOrganization } from "store/actions/user";

// ─── Color Picker Field ────────────────────────────────────────────────────────

const ColorPickerField = ({ label, value, onChange, disabled }) => {
  const handleHexChange = (e) => {
    const raw = e.target.value;
    if (/^#?[0-9A-Fa-f]{0,6}$/.test(raw)) {
      onChange(raw.startsWith("#") ? raw : `#${raw}`);
    }
  };

  return (
    <Box>
      {label && (
        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.75, color: "#666", display: "block" }}>
          {label}
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title="Choisir une couleur">
          <Box
            sx={{
              position: "relative",
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: value || "#cccccc",
              border: "2.5px solid #fff",
              boxShadow: "0 0 0 1.5px #ddd",
              flexShrink: 0,
              transition: "box-shadow 0.2s",
              overflow: "hidden",
              "&:hover": disabled ? {} : { boxShadow: "0 0 0 2px #0001FC" },
            }}
          >
            <input
              type="color"
              value={value && value.length === 7 ? value : "#cccccc"}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: disabled ? "default" : "pointer",
                border: "none",
                padding: 0,
              }}
            />
          </Box>
        </Tooltip>
        <TextField
          size="small"
          value={value || ""}
          onChange={handleHexChange}
          disabled={disabled}
          inputProps={{ maxLength: 7, style: { fontFamily: "monospace", fontSize: 13 } }}
          sx={{ maxWidth: 110 }}
          placeholder="#000000"
        />
      </Stack>
    </Box>
  );
};

// ─── Gradient Row ─────────────────────────────────────────────────────────────

const GradientRow = ({ sectionLabel, color1, color2, onChange1, onChange2, disabled }) => (
  <Box>
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: 0.5, fontSize: 11 }}>
        {sectionLabel}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: 28,
          borderRadius: 1.5,
          background: `linear-gradient(90deg, ${color1 || "#ccc"}, ${color2 || "#eee"})`,
          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)",
        }}
      />
    </Stack>
    <Stack direction="row" spacing={2}>
      <ColorPickerField
        label="Départ"
        value={color1}
        onChange={onChange1}
        disabled={disabled}
      />
      <ColorPickerField
        label="Fin"
        value={color2}
        onChange={onChange2}
        disabled={disabled}
      />
    </Stack>
  </Box>
);

// ─── Section Card ─────────────────────────────────────────────────────────────

const SectionCard = ({ title, children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: 3,
      border: "1px solid #ebebeb",
      overflow: "hidden",
      height: "100%",
      ...sx,
    }}
  >
    <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, bgcolor: "#fafafa", borderBottom: "1px solid #ebebeb" }}>
      <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: "#333" }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
      {children}
    </Box>
  </Box>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const Personalization = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.user.userDetails);
  const userProfile = useSelector((state) => state.user.userProfile);
  const isLoadingFile = useSelector((state) => state.programs.isLoadingFile);

  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);

  const isOwner = userDetails?.type === "OWNER";
  const org = userProfile?.organization || {};

  useEffect(() => {
    dispatch(setHeaderDetails({ title: "Personnalisation" }));
    if (!userProfile) {
      dispatch(getProfile());
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (org.logo) setLogoPreview(org.logo);
  }, [org.logo]);

  const initialValues = {
    primary_color: org.primary_color || "#4A93FF",
    secondary_color: org.secondary_color || "#0001FC",
    fitness_gradient1: org.fitness_gradient1 || "#0933C6",
    fitness_gradient2: org.fitness_gradient2 || "#4A93FF",
    nutrition_gradient1: org.nutrition_gradient1 || "#39A887",
    nutrition_gradient2: org.nutrition_gradient2 || "#95BB2A",
  };

  const handleLogoUpload = async (file, setFieldValue) => {
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    const url = await dispatch(
      uploadFile(file, `/api/upload/coach/thumbs/picture/${userDetails?.org_id}`)
    );
    if (url) setFieldValue("logo", url);
  };

  const handleSubmit = (values) => {
    const payload = { ...values, name: org.name };
    if (!payload.logo) delete payload.logo;
    dispatch(updateOrganization(payload));
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      <Typography variant="body2" sx={{ color: "#777", mb: 3, fontSize: 13, lineHeight: 1.6 }}>
        Vos clients voient votre marque directement dans leur application — logo, couleurs et visuels reflètent votre identité pour une expérience entièrement à votre image.
      </Typography>

      <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, setFieldValue, handleSubmit: submit }) => (
          <form onSubmit={submit}>
            <Grid container spacing={2} sx={{ mb: 2 }}>

              {/* ── Left column: Identity + Colors ──────────────────── */}
              <Grid item xs={12} md={5}>
                <Stack spacing={2}>

                  {/* Brand Identity */}
                  <SectionCard title="Identité de marque">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        onClick={() => isOwner && !isLoadingFile && logoInputRef.current?.click()}
                        sx={{ position: "relative", cursor: isOwner ? "pointer" : "default", flexShrink: 0 }}
                      >
                        <Avatar
                          src={logoPreview}
                          variant="rounded"
                          sx={{
                            width: 72,
                            height: 72,
                            bgcolor: "#f5f5f5",
                            border: "2px dashed #e0e0e0",
                            borderRadius: 2,
                          }}
                        >
                          <ImageOutlinedIcon sx={{ color: "#bbb", fontSize: 28 }} />
                        </Avatar>
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/jpeg,image/png"
                          style={{ display: "none" }}
                          onChange={(e) => handleLogoUpload(e.target.files[0], setFieldValue)}
                        />
                      </Box>
                      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#333", fontSize: 13 }}>
                          Logo de marque
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#999", lineHeight: 1.5 }}>
                          PNG recommandé (fond transparent). 5 Mo max.
                        </Typography>
                        {isOwner && (
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={isLoadingFile}
                            onClick={() => logoInputRef.current?.click()}
                            sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, mt: 0.5, alignSelf: "flex-start" }}
                          >
                            {isLoadingFile ? "Chargement..." : "Modifier"}
                          </Button>
                        )}
                      </Stack>
                    </Stack>

                  </SectionCard>

                  {/* Brand Colors */}
                  <SectionCard title="Couleurs de marque">
                    <Stack spacing={2}>
                      <ColorPickerField
                        label="Couleur principale"
                        value={values.primary_color}
                        onChange={(v) => setFieldValue("primary_color", v)}
                        disabled={!isOwner}
                      />
                      <ColorPickerField
                        label="Couleur secondaire"
                        value={values.secondary_color}
                        onChange={(v) => setFieldValue("secondary_color", v)}
                        disabled={!isOwner}
                      />
                    </Stack>
                  </SectionCard>

                </Stack>
              </Grid>

              {/* ── Right column: Gradients ──────────────────────────── */}
              <Grid item xs={12} md={7}>
                <SectionCard title="Dégradés de couleurs" sx={{ height: "auto" }}>
                  <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 2, lineHeight: 1.5 }}>
                    Ces dégradés sont appliqués dans les sections Fitness et Nutrition de l'application de vos clients.
                  </Typography>

                  <GradientRow
                    sectionLabel="Fitness"
                    color1={values.fitness_gradient1}
                    color2={values.fitness_gradient2}
                    onChange1={(v) => setFieldValue("fitness_gradient1", v)}
                    onChange2={(v) => setFieldValue("fitness_gradient2", v)}
                    disabled={!isOwner}
                  />

                  <Divider sx={{ my: 2.5 }} />

                  <GradientRow
                    sectionLabel="Nutrition"
                    color1={values.nutrition_gradient1}
                    color2={values.nutrition_gradient2}
                    onChange1={(v) => setFieldValue("nutrition_gradient1", v)}
                    onChange2={(v) => setFieldValue("nutrition_gradient2", v)}
                    disabled={!isOwner}
                  />
                </SectionCard>
              </Grid>
            </Grid>

            {/* ── Content Personalization Callout ──────────────────── */}
            <Box
              sx={{
                borderRadius: 3,
                background: "linear-gradient(135deg, #f0f4ff 0%, #f6fff9 100%)",
                border: "1px solid #dde8ff",
                px: { xs: 2, sm: 2.5 },
                py: 2,
                mb: 2.5,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <CheckCircleOutlineIcon sx={{ color: "#119C83", fontSize: 18 }} />
                <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: "#1a1a1a" }}>
                  Images de contenu personnalisées — incluses
                </Typography>
                <Box sx={{ bgcolor: "#119C83", color: "#fff", fontSize: "10px", fontWeight: 700, px: 0.75, py: 0.25, borderRadius: 0.75, letterSpacing: 0.5, lineHeight: 1.5 }}>
                  INCLUS
                </Box>
              </Stack>
              <Typography variant="body2" sx={{ color: "#666", mb: 1.5, fontSize: 13, lineHeight: 1.6, maxWidth: 680 }}>
                D'autres plateformes facturent cette option en supplément. Ici, vous pouvez ajouter une image personnalisée à chaque séance, programme fitness ou programme nutrition — directement depuis leur page de création.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {[
                  { label: "Images de séances", color: "#0001FC" },
                  { label: "Programmes fitness", color: "#0001FC" },
                  { label: "Programmes nutrition", color: "#119C83" },
                ].map(({ label, color }) => (
                  <Stack key={label} direction="row" spacing={0.75} alignItems="center"
                    sx={{ bgcolor: "#fff", border: "1px solid #e4e4e4", borderRadius: 1.5, px: 1.5, py: 0.75 }}
                  >
                    <ImageOutlinedIcon sx={{ color, fontSize: 16 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#333" }}>
                      {label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* ── Save Button ──────────────────────────────────────── */}
            {isOwner && (
              <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    px: 5,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Sauvegarder
                </Button>
              </Box>
            )}
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default Personalization;
